import { t } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import { UI_FONT_STACK, roleFontSize, withUiScale } from "./uiTokens.ts";

/**
 * Wave Indicator — "PRESS SPACE — WAVE N" prompt between waves.
 * Shown during BUILD phase, hidden during COMBAT/DRAFT.
 * Layer 4 (HTML/CSS).
 */
export class WaveIndicator {
  private el: HTMLDivElement;
  private startBtn: HTMLButtonElement;
  private countdownEl: HTMLDivElement;
  private headlineEl: HTMLDivElement;
  private hintEl: HTMLDivElement;
  private onStartWave: (() => void) | null = null;

  constructor(parent: HTMLElement) {
    this.el = document.createElement("div");
    this.el.id = "wave-indicator";

    // BL-211 (S189) — reframe the dead "PRESS SPACE" wait as an inviting BUILD
    // PHASE: a headline + (first-run) deploy nudge above a punchy START WAVE CTA.
    this.headlineEl = document.createElement("div");
    this.headlineEl.id = "wave-build-headline";
    this.el.appendChild(this.headlineEl);

    this.hintEl = document.createElement("div");
    this.hintEl.id = "wave-build-hint";
    this.el.appendChild(this.hintEl);

    this.countdownEl = document.createElement("div");
    this.countdownEl.id = "wave-countdown";
    this.countdownEl.style.display = "none";
    this.el.appendChild(this.countdownEl);

    this.startBtn = document.createElement("button");
    this.startBtn.id = "wave-start-btn";
    this.startBtn.addEventListener("click", () => this.onStartWave?.());

    this.el.appendChild(this.startBtn);
    this.injectStyles();
    parent.appendChild(this.el);
    this.el.style.display = "none";
  }

  show(nextWave: number, onStartWave?: () => void): void {
    if (onStartWave) this.onStartWave = onStartWave;
    this.headlineEl.textContent = t("wave.build.phase");
    // The "deploy your defenses" nudge is the first-30s onboarding cue — show it
    // on the very first build phase, then step out of the way on later waves.
    if (nextWave <= 1) {
      this.hintEl.textContent = t("wave.build.hint");
      this.hintEl.style.display = "block";
    } else {
      this.hintEl.style.display = "none";
    }
    this.startBtn.textContent = t("wave.indicator", { value: nextWave });
    this.el.style.display = "flex";
  }

  hide(): void {
    this.el.style.display = "none";
  }

  /** CR-9: Update countdown display */
  setCountdown(seconds: number, wave: number): void {
    if (seconds > 0) {
      this.countdownEl.style.display = "block";
      this.countdownEl.textContent = String(Math.ceil(seconds));
      const bonus = 25 + wave * 5;
      this.startBtn.textContent = t("countdown.hint", { value: bonus });
    } else {
      this.countdownEl.style.display = "none";
    }
  }

  private injectStyles(): void {
    if (document.getElementById("wave-indicator-styles")) return;
    const style = document.createElement("style");
    style.id = "wave-indicator-styles";
    style.textContent = `
      #wave-indicator {
        position: absolute;
        bottom: 40px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        user-select: none;
        z-index: 10;
      }
      /* BL-211 — BUILD PHASE headline + first-run deploy nudge above the CTA.
         Static text (no animation) → reduced-motion safe by construction. */
      #wave-build-headline {
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("label")};
        color: ${COLORS.SENTINEL_GREEN};
        letter-spacing: 4px;
        text-transform: uppercase;
        opacity: 0.9;
      }
      #wave-build-hint {
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("micro")};
        color: ${COLORS.TEXT_SECONDARY};
        letter-spacing: 2px;
        text-transform: uppercase;
      }
      #wave-start-btn {
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("body")};
        color: ${COLORS.SENTINEL_GREEN};
        background: transparent;
        border: 1px solid ${COLORS.SENTINEL_GREEN};
        padding: 10px 24px;
        letter-spacing: 3px;
        cursor: pointer;
        animation: wave-blink 1.2s ease-in-out infinite;
        transition: all 0.2s;
      }
      #wave-start-btn:hover {
        background: ${COLORS.SENTINEL_GREEN};
        color: ${COLORS.BG_PRIMARY};
        box-shadow: 0 0 12px ${COLORS.SENTINEL_GREEN};
        animation: none;
      }
      /* BL-193 — 44px touch floor (cf. TowerSelector.ts:795-798, the project
         pattern). Coarse-pointer only; fine-pointer layout unchanged. */
      @media (pointer:coarse) {
        #wave-start-btn { min-height: 44px; }
      }
      @keyframes wave-blink {
        0%, 100% { opacity: 0.8; }
        50% { opacity: 0.3; }
      }
      /* BL-211 — reduced-motion: freeze the CTA blink to a static, legible
         button (the prompt's no-pulse-under-reduced-motion discipline). */
      @media (prefers-reduced-motion: reduce) {
        #wave-start-btn { animation: none; opacity: 0.9; }
      }
      #wave-countdown {
        font-family: ${UI_FONT_STACK};
        font-size: ${withUiScale(48)};
        color: ${COLORS.AMBER_WARN};
        text-shadow: 0 0 16px ${COLORS.AMBER_WARN};
        letter-spacing: 4px;
        text-align: center;
      }
    `;
    document.head.appendChild(style);
  }
}
